import {
  ConnectionType,
  Dropdown,
  Flex,
  FormControlSizes,
  IconName,
  IndicatorBadgeWrapper,
  useDropdownPopper,
  type Security,
} from '@talos/kyoko';
import { EllipsisBoxWithTooltip } from '@talos/kyoko/src/components/Form/BaseSelect/EllipsisBoxWithTooltip';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSecurityMarkets } from '../../../../hooks/useSecurityMarkets';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';
import { AddMarketButton } from '../styles';
import { MarketsSelector } from './MarketSelector';

interface SelectMarketsProps {
  security: Security;
}

export function AddMarket({ security }: SelectMarketsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    selectedExchangeMarkets,
    selectedCombined,
    selectedMarkets,
    setSelectedMarkets,
    selectedMarketAccounts,
    setSelectedMarketAccounts,
  } = useDeepDiveContext();
  const { getSecurityMarkets } = useSecurityMarkets({
    markets: security?.Markets ?? [],
    showInactiveMarketAccounts: false,
  });
  const availableSecurityMarkets = useMemo(() => {
    return getSecurityMarkets(ConnectionType.MarketData);
  }, [getSecurityMarkets]);

  const canSelectCount = useMemo(
    () => availableSecurityMarkets.length - selectedCombined.length,
    [availableSecurityMarkets, selectedCombined]
  );

  const handleAddMarketClick = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    const el = e.target;
    if (addMarketBtnRef.current?.contains(el as Node | null)) {
      return false;
    }
    setIsOpen(false);
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  const addMarketBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownPopper = useDropdownPopper({
    isOpen,
    referenceElement: addMarketBtnRef.current,
    dropdownWidth: '240px',
    dropdownPlacement: 'bottom-start',
    onClickOutside: handleClickOutside,
  });

  return (
    <Flex alignItems="center" overflow="hidden">
      <AddMarketButton
        ref={addMarketBtnRef}
        startIcon={IconName.Plus}
        onClick={handleAddMarketClick}
        size={FormControlSizes.Small}
        background="gray.010"
      >
        <IndicatorBadgeWrapper content={canSelectCount}>Add Market</IndicatorBadgeWrapper>
      </AddMarketButton>
      {!selectedExchangeMarkets.length && (
        <EllipsisBoxWithTooltip minWidth={300} fontSize="fontSizeSmall" px="spacing8" flex="0 1 auto">
          With no exchange selected, the DeepDive is displaying market data for all exchanges.
        </EllipsisBoxWithTooltip>
      )}

      <Dropdown {...dropdownPopper}>
        <MarketsSelector
          security={security}
          ref={inputRef}
          selectedMarkets={selectedMarkets}
          setSelectedMarkets={setSelectedMarkets}
          selectedMarketAccounts={selectedMarketAccounts}
          setSelectedMarketAccounts={setSelectedMarketAccounts}
        />
      </Dropdown>
    </Flex>
  );
}
